import { createContext, useContext, useEffect, useRef, useState } from 'react';

import { checkBackendStatus } from '../actions/backendVersionActions';
import { checkRunningTests } from '../hooks/useCheckRunningTests';
import useInterruptedTestRecovery from '../hooks/useInterruptedTestRecovery';
import {
  shouldPollVersion,
  shouldPollRunning,
  shouldPollDeviceStatus,
  TestWorkflowState,
  TestWorkflowAction,
} from '../constants/testWorkflowState';
import { fetchDeviceDiagnostics } from '../components/Device/service/settingsService';
import logger from '../utils/logger';
import logError from '../utils/logError';

import { useAppWorkflowContext } from './AppWorkflowContext';
import { useAuthContext } from './AuthContext';
import { useCognitoContext } from './CognitoContext';
import { useAppConfig } from './AppConfigContext.jsx';
import { useAppContext } from './appContext.jsx';

const PollingManagerContext = createContext(null);

export const PollingManagerProvider = ({ children }) => {
  const { workflowState, dispatchWorkflow } = useAppWorkflowContext();
  const { isAuthenticated } = useAuthContext();
  const { signOut } = useCognitoContext();
  const { api } = useAppConfig();
  const { globalHealthStatus } = useAppContext();
  const acquireBaseUrl = api.acquire.baseUrl;
  const testBaseUrl = api.test.baseUrl;
  const requestRecoveries = useInterruptedTestRecovery(testBaseUrl, isAuthenticated);

  const [backendStatus, setBackendStatus] = useState(null);
  const [runningState, setRunningState] = useState(null);

  const [deviceStatus, setDeviceStatus] = useState(null);
  const [deviceStatusError, setDeviceStatusError] = useState(null);
  const [devicePollingCycle, setDevicePollingCycle] = useState(0);
  const [devicePollingTick, setDevicePollingTick] = useState(0);

  const [testsPollingCycle, setTestsPollingCycle] = useState(0);
  const [testsPollingTick, setTestsPollingTick] = useState(0);

  const versionIntervalRef = useRef(null);
  const runningIntervalRef = useRef(null);
  const deviceIntervalRef = useRef(null);

  const clearRef = (ref) => {
    if (ref.current) {
      clearInterval(ref.current);
      ref.current = null;
    }
  };

  // Load /version and /running once on app init
  useEffect(() => {
    if (
      workflowState !== TestWorkflowState.APP_INIT ||
      !isAuthenticated ||
      globalHealthStatus !== 'up'
    ) {
      return;
    }

    // A request may finish after logout or a health change. Ignore that stale result.
    let isActive = true;

    Promise.all([checkBackendStatus(acquireBaseUrl), checkRunningTests(testBaseUrl)])
      .then(([version, running]) => {
        if (!isActive) return;

        setBackendStatus(version);
        setRunningState(running);
        // Recovery runs independently so it cannot block polling or workflow updates.
        requestRecoveries(running?.processing).catch((error) => {
          logger.error(logError(error, { action: 'pollingManagerInitialRecovery' }));
        });

        if (version?.version === 'Not able to read version.') {
          dispatchWorkflow({ type: TestWorkflowAction.ENTER_BACKEND_UPDATING });
          return;
        }

        if (running?.isTestNotFailedAndNotReported === false) {
          dispatchWorkflow({ type: TestWorkflowAction.ENTER_WAITING_FOR_PROCESSING });
        } else {
          dispatchWorkflow({ type: TestWorkflowAction.ENTER_IDLE });
        }
      })
      .catch((err) => {
        if (!isActive) return;

        logger.error(logError(err, { action: 'pollingManagerInit' }));
        const isUpdatePersisted = localStorage.getItem('showUpdateInProgressModal') === 'true';
        if (isUpdatePersisted) {
          dispatchWorkflow({ type: TestWorkflowAction.ENTER_BACKEND_UPDATING });
          return;
        }
        dispatchWorkflow({ type: TestWorkflowAction.ENTER_IDLE });
      });

    return () => {
      isActive = false;
    };
  }, [
    workflowState,
    isAuthenticated,
    globalHealthStatus,
    acquireBaseUrl,
    testBaseUrl,
    requestRecoveries,
  ]);

  // Poll /version based on workflow state
  useEffect(() => {
    if (!isAuthenticated) return;

    clearRef(versionIntervalRef);

    if (!shouldPollVersion(workflowState)) {
      return;
    }

    const intervalMs = workflowState === TestWorkflowState.BACKEND_UPDATING ? 2000 : 30000;

    const pollVersion = () => {
      checkBackendStatus(acquireBaseUrl)
        .then((status) => {
          setBackendStatus(status);
        })
        .catch((err) => {
          logger.error(logError(err, { action: 'pollingManagerVersionTick' }));
          const isRebooting = localStorage.getItem('sonda.backend.rebooting') === 'true';
          const isUpdateInProgress = localStorage.getItem('showUpdateInProgressModal') === 'true';

          const isRebootingOrUpdating = isRebooting || isUpdateInProgress;
          if (
            err?.response?.status &&
            (err.response.status === 401 || err.response.status === 403) &&
            !isRebootingOrUpdating
          ) {
            signOut();
          }
          setBackendStatus(null);
        });
    };

    pollVersion();
    versionIntervalRef.current = setInterval(pollVersion, intervalMs);

    return () => clearRef(versionIntervalRef);
  }, [workflowState, isAuthenticated, acquireBaseUrl]);

  // Poll /running only when WAITING_FOR_PROCESSING
  useEffect(() => {
    if (!isAuthenticated || globalHealthStatus !== 'up') return;

    clearRef(runningIntervalRef);

    if (!shouldPollRunning(workflowState)) {
      return;
    }

    // Clearing the interval does not cancel a request already in progress.
    let isActive = true;
    // Skip a tick if the previous poll is still running (avoids out-of-order results).
    let isPolling = false;
    const intervalMs = 2000;
    setTestsPollingCycle((cycle) => cycle + 1);
    setTestsPollingTick(0);

    const pollRunning = () => {
      if (isPolling) return;
      isPolling = true;
      checkRunningTests(testBaseUrl)
        .then((data) => {
          if (!isActive) return;

          setRunningState(data);
          // A slow recovery request must not stop the two-second running-test poll.
          requestRecoveries(data?.processing).catch((error) => {
            logger.error(logError(error, { action: 'pollingManagerRecoveryTick' }));
          });

          setTestsPollingTick((tick) => tick + 1);

          if (data?.isTestNotFailedAndNotReported === true) {
            clearRef(runningIntervalRef);
            dispatchWorkflow({ type: TestWorkflowAction.ENTER_IDLE });
          }
        })
        .catch((err) => {
          if (isActive) {
            logger.error(logError(err, { action: 'pollingManagerRunningTick' }));
          }
        })
        .finally(() => {
          isPolling = false;
        });
    };

    pollRunning();
    runningIntervalRef.current = setInterval(pollRunning, intervalMs);

    return () => {
      isActive = false;
      clearRef(runningIntervalRef);
    };
  }, [workflowState, isAuthenticated, globalHealthStatus, testBaseUrl, requestRecoveries]);

  // Poll /diagnostics/device based on workflow state
  useEffect(() => {
    if (!isAuthenticated) return;

    clearRef(deviceIntervalRef);

    if (!shouldPollDeviceStatus(workflowState)) {
      return;
    }

    const intervalMs = 3000;
    setDevicePollingCycle((cycle) => cycle + 1);
    setDevicePollingTick(0);

    const pollDeviceStatus = () => {
      return fetchDeviceDiagnostics(acquireBaseUrl)
        .then((data) => {
          setDeviceStatus(data);
          setDeviceStatusError(null);
          setDevicePollingTick((tick) => tick + 1);
        })
        .catch((err) => {
          logger.error(logError(err, { action: 'pollingManagerDeviceStatusTick' }));
          const statusCode = err?.response?.status ?? null;
          const data = err?.response?.data ?? null;
          setDeviceStatus(null);
          setDeviceStatusError({ statusCode, data, error: err });
        });
    };

    pollDeviceStatus();
    deviceIntervalRef.current = setInterval(pollDeviceStatus, intervalMs);

    return () => clearRef(deviceIntervalRef);
  }, [workflowState, isAuthenticated, acquireBaseUrl]);

  const value = {
    backendStatus,
    runningState,
    testsPollingCycle,
    testsPollingTick,
    deviceStatus,
    deviceStatusError,
    devicePollingCycle,
    devicePollingTick,
  };

  return <PollingManagerContext.Provider value={value}>{children}</PollingManagerContext.Provider>;
};

export const usePollingManager = () => useContext(PollingManagerContext);
